import React, { useEffect, useRef, useState } from 'react';
import { DeliveryOption, PrintSize } from '../../prop-types';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { allPhotosCollectedByRootImage } from '../../common/collect-by-root-image';
import {
  sizesWithPrice,
  sizesWithRootImage,
  totalQuantity,
} from '../../common/totalizing-mixin';
import Photo from '../photos-grid/photo';
import PhotoFilename from '../../common/photo-filename';
import findRecommendedPrintSize from '../../common/photo-quality/recommended-print-size';
import Paginator from '../../../_utils/paginator';
import { usePagination } from '../../../_utils/paginator/use-pagination';
import pluralize from '../../../_utils/tools/pluralize';
import PrintCountDisplay from '../print-count-display';
import Button from '../../../_common/button';
import SideMenu from './side-menu';
import ResizeOptionsModal from '../resize-options-modal';
import Footer from '../footer';
import { builderDispatch } from '../../../_utils/ui';
import { UpdatePhotoEvents } from '../../common/events/event-declarations';

const modalType = {
  NONE: undefined,
  CHANGE_SIZE: 'change_size',
  ADD_SIZE: 'add_size',
};

export default function PhotosGrid({
  sizes,
  availableSizes,
  viewingSizeKey,
  onAdvancedEditPhoto,
  deliveryOption,
  orderedPhotos,
  invalidDeliveryOptionSelected,
  handleAddPhotos,
}) {
  const PHOTOS_PER_PAGE = 25;
  const recommendedPrintSizes = findAllRecommendedPrintSizes();
  const totalPhotosLength = totalQuantity(orderedPhotos);
  const {
    page,
    paginatedItems,
    totalPageCount,
    handlePageNext,
    handlePagePrevious,
    handleGoToPage,
  } = usePagination({
    items: orderedPhotos,
    itemsPerPage: PHOTOS_PER_PAGE,
  });
  const defaultSelectedPhoto = {
    raw_dimensions: { w: 1000, h: 1000 },
    root_image_id: undefined,
  };
  const selectedPhotoRef = useRef(defaultSelectedPhoto);
  const [selectedModal, setSelectedModal] = useState(modalType.NONE);
  const selectedPhoto = selectedPhotoRef.current;

  useEffect(() => {
    handleGoToPage(1);
  }, [viewingSizeKey]); // reset page if size filter changes

  function findAllRecommendedPrintSizes() {
    const photosCollection = allPhotosCollectedByRootImage(sizes);

    return Object.entries(photosCollection).reduce(
      (acc, [id, photoSizes = {}]) => {
        const photo = (Object.values(photoSizes)[0] || [])[0];
        acc[id] = findRecommendedPrintSize(availableSizes, photo);
        return acc;
      },
      {}
    );
  }

  function addSize(targetSizeKey) {
    builderDispatch.dispatch({
      type: UpdatePhotoEvents.ADD_TO_SIZE,
      root_image_id: selectedPhoto.root_image_id,
      targetSizeKey,
      quantity: 1,
    });
  }

  function handleCloseModal() {
    selectedPhotoRef.current = defaultSelectedPhoto;
    setSelectedModal(modalType.NONE);
  }

  const handleSizeChange = targetSizeKey => {
    builderDispatch.dispatch({
      type: UpdatePhotoEvents.RESIZE,
      image_id: selectedPhoto.image_id,
      size_key: selectedPhoto.size.key,
      targetSizeKey,
    });
  };

  const modalOptions = {
    [modalType.CHANGE_SIZE]: {
      title: 'Change Size',
      onClick: targetSizeKey => handleSizeChange(targetSizeKey),
    },
    [modalType.ADD_SIZE]: {
      title: 'Add Size',
      onClick: targetSizeKey => addSize(targetSizeKey),
    },
  };

  return (
    <>
      <div className="list-view list-view-mobile">
        {paginatedItems.map(({ id, photos = [], isLastPhoto }) => (
          <section
            key={`photo_${id}`}
            className={classNames('d-flex flex-column list-item', {
              last: isLastPhoto,
            })}>
            <PhotoFilename photo={photos[0]} />
            <Button
              className="btn btn-sm btn-link list-view-mobile__add-size add-size"
              type="button"
              text="Add Size"
              onClick={() => {
                selectedPhotoRef.current = photos[0];
                setSelectedModal(modalType.ADD_SIZE);
              }}
            />
            {photos.map((photo, index) => (
              <div
                key={`${id}_${photo.size.size_id}`}
                className="list-item__size row">
                <div className="col-6 list-item__size__photo">
                  {Photo(
                    photo,
                    isLastPhoto,
                    sizesWithRootImage(sizes, id),
                    availableSizes,
                    onAdvancedEditPhoto,
                    recommendedPrintSizes[id]
                  )}
                </div>
                <div className="col-6 list-item__size__side-menu">
                  <SideMenu
                    photo={photo}
                    handleResizeOptionsModal={() => {
                      selectedPhotoRef.current = photo;
                      setSelectedModal(modalType.CHANGE_SIZE);
                    }}
                  />
                </div>
              </div>
            ))}
          </section>
        ))}
        {selectedModal && (
          <ResizeOptionsModal
            title={modalOptions[selectedModal].title}
            availableSizes={sizesWithPrice(
              availableSizes,
              sizes,
              deliveryOption
            )}
            sizesWithPrints={sizesWithRootImage(
              sizes,
              selectedPhoto.root_image_id
            )}
            onClick={targetSizeKey => {
              modalOptions[selectedModal].onClick(targetSizeKey);
              handleCloseModal();
            }}
            showResizeOptionsModal={!!selectedModal}
            deliveryOption={deliveryOption}
            handleResizeOptionsModal={handleCloseModal}
            photoDimensions={selectedPhoto.raw_dimensions}
            recommendedPrintSize={
              recommendedPrintSizes[selectedPhoto.root_image_id]
            }
            showLowQualityWarning
            hideTooltip
          />
        )}
        <Footer>
          <Paginator
            title="Uploaded photos pages"
            page={page}
            totalPageCount={totalPageCount}
            scrollTarget={-200}
            onPageNext={handlePageNext}
            onPagePrevious={handlePagePrevious}
            onGoToPage={handleGoToPage}
          />
          <div className="w-100 d-flex align-items-center justify-content-between">
            <PrintCountDisplay
              hideTotal
              selectedCount={totalPhotosLength}
              totalCount={totalPhotosLength}
              text={pluralize('photo', totalPhotosLength)}
            />
            <Button
              className="btn btn-link btn-underline"
              onClick={handleAddPhotos}
              disabled={invalidDeliveryOptionSelected}>
              + Add more photos
            </Button>
          </div>
        </Footer>
      </div>
    </>
  );
}

PhotosGrid.propTypes = {
  deliveryOption: DeliveryOption,
  availableSizes: PropTypes.arrayOf(PrintSize),
  sizes: PropTypes.arrayOf(PrintSize),
}.isRequired;
